import React, { useState } from 'react';
import { Target, ExternalLink } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { resolveDealId } from '@/lib/api/myTenders';
import PilotTile, { TileTabs, TileEmpty, TileSkeleton } from './PilotTile';
import DealStatusMenu from './DealStatusMenu';
import { FitLightBadge, DeadlineBadge } from './pilotBadges';

const LIMIT = 5;

function sourceUrl(deal) {
  const u = deal?.source_url || '';
  return /^https?:\/\//i.test(u) ? u : null;
}

function DealRow({ deal, deals, tenantId }) {
  const id = resolveDealId(deal);
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 rounded-lg p-2 hover:bg-gray-50">
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        {/* Same as the Top Signals tile: the title links to the tender notice. */}
        {sourceUrl(deal) ? (
          <a
            href={sourceUrl(deal)}
            target="_blank"
            rel="noopener noreferrer"
            title={deal.title || ''}
            className="flex items-center gap-1 text-sm font-medium text-[#0A1A2F] underline decoration-[#00C2C2] underline-offset-2 hover:text-[#00C2C2]"
          >
            <span className="truncate">{deal.title || 'Untitled tender'}</span>
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-[#00C2C2]" aria-hidden="true" />
          </a>
        ) : (
          <p className="truncate text-sm font-medium text-[#0A1A2F]" title={deal.title || ''}>{deal.title || 'Untitled tender'}</p>
        )}
        <p className="truncate text-xs text-[#5C6B7A]">{deal.institution || deal.account_name || '—'}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <FitLightBadge label={deal.fit_light_label} tenantId={tenantId} dealId={deal.fit_deal_id || id} />
          <DeadlineBadge deal={deal} />
        </div>
      </div>
      {deals.enabled ? (
        <DealStatusMenu
          dealId={id}
          status={deals.augment[id]?.status || null}
          onChange={deals.changeStatus}
          saving={deals.savingId === id}
        />
      ) : null}
    </div>
  );
}

export default function LiveDealsTile({ deals, tenantId, myDealsLabel = 'My Deals' }) {
  const [tab, setTab] = useState('live');
  const rows = tab === 'live' ? deals.goodFitLive : deals.myDeals;
  const href = tab === 'live' ? createPageUrl('Procurement') : createPageUrl('Procurement?tab=my-tenders');
  const tabs = [{ value: 'live', label: `Live deals (${deals.loading ? '…' : deals.goodFitLive.length})` }];
  if (deals.enabled || deals.loading) {
    tabs.push({ value: 'mine', label: `${myDealsLabel} (${deals.loading || deals.closedLoading ? '…' : deals.myDeals.length})` });
  }

  return (
    <PilotTile
      testId="pilot-tile-live-deals"
      icon={Target}
      title="Live Deals"
      href={href}
      right={<TileTabs tabs={tabs} value={tab} onChange={setTab} label="Deals view" />}
    >
      {deals.loading || (tab === 'mine' && deals.closedLoading && rows.length === 0) ? (
        <TileSkeleton />
      ) : deals.error ? (
        <TileEmpty>{deals.error}</TileEmpty>
      ) : rows.length === 0 ? (
        <TileEmpty>{tab === 'live' ? 'No Strong or Moderate Fit tenders open right now.' : 'No Strong or Moderate Fit deals in your categories.'}</TileEmpty>
      ) : (
        rows.slice(0, LIMIT).map((deal) => <DealRow key={resolveDealId(deal)} deal={deal} deals={deals} tenantId={tenantId} />)
      )}
    </PilotTile>
  );
}
